import React, { useState } from 'react'
import { Button } from 'antd'
import { useNavigate, useLocation } from 'react-router'
import axios from 'axios'
import { toast } from 'sonner'
import loginBackground from '@/assets/images/login_background.png'
import valiantlytixLogo from '@/assets/images/darkLogo.png'
import logo from '@/assets/images/vantageAI.png'

const NewEmailVerifyPage = () => {

  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email || localStorage.getItem("verification_email");
  const { VITE_APP_URL: URL } = import.meta.env;
  const [resending, setResending] = useState(false);

  const handleResendEmail = async () => {
    if (!email) {
      toast.error("Email not found. Please try signing up again.");
      return;
    }

    try {
      setResending(true);
      await axios.post(`${URL}/api/users/resend-verification-email`, { email });
      toast.success("Verification email resent to " + email);
    } catch (err) {
      console.error(err);
      toast.error("Failed to resend verification email.");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className='flex w-full h-screen bg-[#F8F9FA] font-sans'>
      {/* Left side: Form Panel */}
      <div className='w-full lg:w-1/2 h-full flex flex-col items-center relative px-4 sm:px-8 overflow-y-auto'>
        
        <div className='w-full max-w-[440px] flex flex-col items-center my-auto py-8'>
          {/* Logo at Top */}
          <div className='w-full flex justify-center mb-8'>
            <img src={logo} alt="Vantage AI Logo" className='w-[160px] md:w-[200px] h-auto object-contain' />
          </div>

          {/* Form Container */}
          <div className='w-full bg-white px-6 sm:px-8 py-8 sm:py-10 rounded-2xl shadow-sm border border-gray-100 flex flex-col'>
            <div className='flex flex-col gap-2 mb-6'>
              <div className='flex items-center gap-3'>
                <svg width="13" height="18" viewBox="0 0 13 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M1.56888 17.85C1.13546 17.85 0.765625 17.6958 0.459375 17.3873C0.153125 17.0789 0 16.7081 0 16.275V7.875C0 7.44187 0.154219 7.07109 0.462656 6.76266C0.771094 6.45422 1.14188 6.3 1.575 6.3H2.1V4.2C2.1 3.038 2.51001 2.0475 3.33003 1.2285C4.14991 0.409499 5.14157 0 6.30503 0C7.46834 0 8.45833 0.409499 9.275 1.2285C10.0917 2.0475 10.5 3.038 10.5 4.2V6.3H11.025C11.4581 6.3 11.8289 6.45422 12.1373 6.76266C12.4458 7.07109 12.6 7.44187 12.6 7.875V16.275C12.6 16.7081 12.4457 17.0789 12.1371 17.3873C11.8285 17.6958 11.4575 17.85 11.0241 17.85H1.56888ZM1.575 16.275H11.025V7.875H1.575V16.275ZM6.30459 13.65C6.73903 13.65 7.10938 13.4953 7.41563 13.186C7.72188 12.8767 7.875 12.5048 7.875 12.0704C7.875 11.636 7.72034 11.2656 7.41103 10.9594C7.10172 10.6531 6.72984 10.5 6.29541 10.5C5.86097 10.5 5.49063 10.6547 5.18438 10.964C4.87813 11.2733 4.725 11.6452 4.725 12.0796C4.725 12.514 4.87966 12.8844 5.18897 13.1906C5.49828 13.4969 5.87016 13.65 6.30459 13.65ZM3.675 6.3H8.925V4.2C8.925 3.47083 8.66979 2.85104 8.15938 2.34063C7.64896 1.83021 7.02917 1.575 6.3 1.575C5.57083 1.575 4.95104 1.83021 4.44063 2.34063C3.93021 2.85104 3.675 3.47083 3.675 4.2V6.3Z" fill="#A51C1C"/>
                </svg>
                <span className='text-xl md:text-2xl font-semibold tracking-[0.2px] text-gray-900'>Verify your Email</span>
              </div>
              <span className='text-gray-500 text-sm'>
                <strong>Note:</strong> We've sent a verification email to your inbox.<br />
                Please open the email and click the verification link to activate your account.<br />
                If you don't receive the email within a few minutes, check your spam or junk folder.
              </span>
            </div>

            <Button type='primary' size='large' onClick={() => navigate("/login")} className='w-full h-12 mt-2 text-base font-medium !bg-[#A91D22] hover:!bg-[#8a171b] !border-none rounded-lg shadow-md transition-all flex items-center justify-center'>
              Continue to Sign In
            </Button>

            <div className="flex items-center my-6">
              <div className="flex-grow border-t border-gray-200"></div>
              <span className="flex-shrink-0 mx-4 text-gray-400 text-[13px]">Didn't receive an email?</span>
              <div className="flex-grow border-t border-gray-200"></div>
            </div>

            <Button 
              size='large' 
              onClick={handleResendEmail} 
              loading={resending}
              className='w-full h-12 text-base font-medium !text-[#A91D22] border !border-[#A91D22] !bg-[#fdf2f2] hover:!bg-[#fce4e4] rounded-lg transition-all flex items-center justify-center'
            >
              Resend Email
            </Button>
          </div>
        </div>

        <span className='text-gray-400 text-xs md:text-sm select-none pb-6 mt-auto'>&copy; 2026 Vantage AI. All rights reserved.</span>
      </div>

      {/* Right side: Image Panel */}
      <div className='hidden lg:flex w-1/2 relative bg-black items-center justify-center overflow-hidden'>
        <img src={loginBackground} alt="Vantage AI Dashboard Graphic" className="absolute inset-0 w-full h-full object-cover opacity-90" />
        
        <div className="absolute top-16 left-16 z-10">
          <div className="text-white text-[42px] leading-[1.2] font-bold tracking-tight">
            <div>Turn raw data into</div>
            <div>professional <span className="text-[#FCA5A5]">Dashboards</span></div>
            <div>in minutes.</div>
          </div>
        </div>

        {/* Valiantlytix Logo Overlay */}
        <div className="absolute bottom-12 right-12 z-10">
          <img src={valiantlytixLogo} alt="Valiantlytix Logo" className="w-[180px] h-auto object-contain" />
        </div>
      </div>
    </div>
  )
}

export default NewEmailVerifyPage